import styled from 'styled-components';
import { useState } from 'react';

import { Choice } from 'shared/components';
import { IConstructorFacetMultiple } from 'shared/types/ConstructorFacet';
import { SIZE_GENDER } from 'search/enums/constants';
import { variableFontRegular } from 'styles/theme';
import {
  sortGender,
  sortSizesByDisplayName,
  transformSizeFacet,
} from 'search/utils/sizeFilterUtils';

interface ISearchFilterSizeMobile {
  facet: IConstructorFacetMultiple;
  selectedFilterValues: Array<string>;
  setSelectedFilterValues: (options: Array<string>) => void;
}

interface ISizeFilterProductType {
  productType: string;
  sizeOptions: Array<IConstructorFacetMultiple>;
  selectedFilterValues: Array<string>;
  setSelectedFilterValues: (options: Array<string>) => void;
}

export const SizeFilterProductType = ({
  productType,
  sizeOptions,
  selectedFilterValues,
  setSelectedFilterValues,
}: ISizeFilterProductType) => {
  const genders = Object.keys(sizeOptions).sort(sortGender);
  const [selectedGender, setSelectedGender] = useState(genders[0]);
  const options = sizeOptions[selectedGender]
    .sort(sortSizesByDisplayName)
    .filter((option) => option.display_name);

  const handleGenderClick = (val) => {
    setSelectedGender(val);
  };

  const handleOnChange = (optionValue) => {
    if (selectedFilterValues.includes(optionValue)) {
      const updatedValues = selectedFilterValues.filter((value) => {
        return value !== optionValue;
      });
      setSelectedFilterValues(updatedValues);
    } else {
      setSelectedFilterValues([...selectedFilterValues, optionValue]);
    }
  };

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <SizeFilterProductType.Wrapper>
      <SizeFilterProductType.HeaderWrapper>
        <SizeFilterProductType.Title data-qa="size_filter_product_type">
          {productType}
        </SizeFilterProductType.Title>
        <SizeFilterProductType.GenderWrapper>
          {genders.map((gender) => (
            <SizeFilterProductType.Gender
              $isSelected={selectedGender === gender}
              data-qa="size_filter_gender"
              key={gender}
              onClick={() => handleGenderClick(gender)}
            >
              {SIZE_GENDER?.[gender] || gender}
            </SizeFilterProductType.Gender>
          ))}
        </SizeFilterProductType.GenderWrapper>
      </SizeFilterProductType.HeaderWrapper>
      <SizeFilterProductType.ChoiceWrapper>
        {options.map((option) => (
          <Choice
            checked={
              Array.isArray(selectedFilterValues) && selectedFilterValues.includes(option.value)
            }
            data-qa="size_filter_size_option"
            key={option.value}
            label={option.display_name}
            onChange={() => handleOnChange(option.value)}
            type="checkbox"
            value={option.value}
          />
        ))}
      </SizeFilterProductType.ChoiceWrapper>
    </SizeFilterProductType.Wrapper>
  );
};

export const SearchFilterSizeMobileDeprecated = ({
  facet,
  selectedFilterValues,
  setSelectedFilterValues,
}: ISearchFilterSizeMobile) => {
  const transformedSizes = transformSizeFacet(facet.options);

  return (
    <>
      {Object.keys(transformedSizes).map((productType) => (
        <SizeFilterProductType
          key={productType}
          productType={productType}
          selectedFilterValues={selectedFilterValues}
          setSelectedFilterValues={setSelectedFilterValues}
          sizeOptions={transformedSizes[productType]}
        />
      ))}
    </>
  );
};

SizeFilterProductType.Wrapper = styled.div`
  margin-bottom: 32px;
`;

SizeFilterProductType.HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

SizeFilterProductType.Title = styled.h3`
  text-transform: capitalize;
  ${variableFontRegular({ size: '11.5px', height: '16px' })};
  font-weight: 400;
  margin: 0;
`;

SizeFilterProductType.GenderWrapper = styled.div`
  display: flex;
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
`;

SizeFilterProductType.Gender = styled.div<{ $isSelected: boolean }>`
  text-transform: capitalize;
  margin-left: 8px;
  text-decoration: ${({ $isSelected }) => $isSelected && 'underline'};
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }
`;

SizeFilterProductType.ChoiceWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(24px, 1fr));
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  margin-top: 16px;

  label {
    width: min-content;
  }
`;
